import React, { useState, useEffect } from 'react'

interface CheckBoxFilterProps {
  title: string
  labelsArray: string[]
  onSelectionChange?: (selections: Record<string, boolean>) => void
  initialSelections?: Record<string, boolean>
  noBg?: string
  disabledLabels?: string[]
}

const CheckBoxFilter: React.FC<CheckBoxFilterProps> = ({
  title,
  labelsArray,
  onSelectionChange,
  initialSelections,
  noBg,
  disabledLabels = [],
}) => {
  // Initialize with provided selections or all unchecked
  const [checkboxSelections, setCheckboxSelections] = useState<
    Record<string, boolean>
  >(
    initialSelections ||
      Object.fromEntries(labelsArray.map((label) => [label, false]))
  )
 
  // Sync when initialSelections prop changes (e.g., when Default button is clicked)
  useEffect(() => {
    if (initialSelections !== undefined) {
      setCheckboxSelections(initialSelections)
    }
  }, [initialSelections])

  // Check if there's an "All" option in the labels (matches "All", "All Request Types", "All Statuses", etc.)
  const allLabel = labelsArray.find((label) => /^all(\s|$)/i.test(label))
  const otherLabels = labelsArray.filter((label) => !/^all(\s|$)/i.test(label))

  const isAllLabel = (label: string) => /^all(\s|$)/i.test(label)

  const handleCheckboxChange = (label: string, isChecked: boolean) => {
    let updated = { ...checkboxSelections, [label]: isChecked }

    const defaultRequestStatuses = [
      'Submitted',
      'Retraction Submitted',
      'Resubmitted',
    ]

    // If "All" is being unchecked
    if (allLabel && isAllLabel(label) && !isChecked) {
      // Legacy behavior for Request Status
      if (title === 'Request Status') {
        updated = Object.fromEntries(
          labelsArray.map((l) => [l, defaultRequestStatuses.includes(l)])
        )
      } else {
        // Existing behavior for other filters
        updated = Object.fromEntries(labelsArray.map((l) => [l, false]))
      }
    }
    // If "All" is being checked, check all other options too
    else if (allLabel && isAllLabel(label) && isChecked) {
      updated = Object.fromEntries(labelsArray.map((l) => [l, true]))
    }
    // If any other option is unchecked, also uncheck "All"
    else if (allLabel && !isChecked && !isAllLabel(label)) {
      updated[allLabel] = false
    }
    // If all other options are checked, also check "All"
    else if (allLabel && isChecked && !isAllLabel(label)) {
      const allOthersChecked = otherLabels.every((l) =>
        l === label ? true : updated[l]
      )
      if (allOthersChecked) {
        updated[allLabel] = true
      }
    }

    setCheckboxSelections(updated)
    onSelectionChange?.(updated)
  }

  // Show scroll only for sections with background (not status/requestType) and more than 5 items
  const needsScroll = !noBg && labelsArray.length > 5

  const renderCheckboxItem = (label: string) => {
    const isDisabled = disabledLabels.includes(label)
    return (
      <div
        key={label}
        className={`flex items-center gap-3 p-2 sm:p-3 ${noBg ? '' : 'even:bg-white odd:bg-[#F4F5F6]'} rounded`}
        style={{ position: 'relative' }}
      >
        <input
          type="checkbox"
          id={`checkbox-${label.replaceAll(/\s+/g, '-').toLowerCase()}`}
          checked={checkboxSelections[label] || false}
          onChange={(e) => handleCheckboxChange(label, e.target.checked)}
          disabled={isDisabled}
          style={{
            width: '16px',
            height: '16px',
            flexShrink: 0,
            position: 'relative',
            cursor: isDisabled ? 'not-allowed' : 'pointer',
            opacity: isDisabled ? 0.5 : 1,
          }}
          aria-label={label}
        />
        <label
          htmlFor={`checkbox-${label.replaceAll(/\s+/g, '-').toLowerCase()}`}
          className="text-xs sm:text-sm cursor-pointer"
          style={{ flex: 1 }}
        >
          {label}
        </label>
      </div>
    )
  }

  return (
    <div className="mt-6! w-full">
      <p className="p3 -medium mb-2! truncate">{title}</p>
      {needsScroll ? (
        <div style={{ display: 'flex' }}>
          <div
            className="always-visible-scrollbar"
            style={{
              maxHeight: '240px',
              overflowY: 'scroll',
              position: 'relative',
              flex: 1,
            }}
          >
            {labelsArray.map(renderCheckboxItem)}
          </div>
          {/* Visual scrollbar track indicator */}
          <div
            style={{
              width: '12px',
              backgroundColor: '#cbd5e1',
              borderRadius: '0 4px 4px 0',
              marginLeft: '-12px',
              pointerEvents: 'none',
            }}
          />
        </div>
      ) : (
        <div>{labelsArray.map(renderCheckboxItem)}</div>
      )}
    </div>
  )
}

export default CheckBoxFilter
